import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { ApiService } from './api.service';

export type PageDataPreloadContext = {
    pageSlug: string;
    pageSegments: string[];
    languageCode: string;
    secondarySlug?: string;
};

export type PreloadedComponentData = {
    component: string;
    data: Record<string, unknown>;
};

type PageDataPreloader = {
    component: string;
    matches: (context: PageDataPreloadContext) => boolean;
    load: (
        context: PageDataPreloadContext,
        apiService: ApiService,
    ) => Observable<Record<string, unknown>>;
};

const PAGE_DATA_PRELOADERS: readonly PageDataPreloader[] = [
    {
        component: 'ComponentMainFullProduct',
        matches: ({ pageSlug, secondarySlug }) =>
            pageSlug === 'hof/produkt' && !!secondarySlug,
        load: ({ secondarySlug }, apiService) =>
            apiService.getProductBySlug(secondarySlug!).pipe(
                map((response) => ({
                    product: response.data[0],
                    productLoaded: true,
                })),
            ),
    },
    {
        component: 'ComponentMainFullBlogPost',
        matches: ({ pageSlug, secondarySlug }) =>
            pageSlug === 'hof/blog-post' && !!secondarySlug,
        load: ({ secondarySlug }, apiService) =>
            apiService
                .getBlogPostBySlug(secondarySlug!)
                .pipe(
                    map((response) => ({
                        blogPost: response.data[0],
                        blogPostLoaded: true,
                    })),
                ),
    },
    {
        component: 'ComponentMainBlog',
        matches: ({ pageSlug }) => pageSlug === 'hof/blog',
        load: (_, apiService) =>
            apiService
                .getBlogPosts()
                .pipe(map((response) => ({ blogPosts: response.data }))),
    },
    {
        component: 'ComponentMainShop',
        matches: ({ pageSlug }) => pageSlug === 'hof/shop',
        load: ({ pageSlug, languageCode }, apiService) =>
            apiService
                .getShopDataByPageSlugGraphql(pageSlug, languageCode)
                .pipe(map((response) => ({ shopData: response }))),
    },
];

@Injectable({ providedIn: 'root' })
export class PageDataPreloaderService {
    private apiService = inject(ApiService);

    preload(
        context: PageDataPreloadContext,
    ): Observable<PreloadedComponentData[]> {
        const preloadRequests = PAGE_DATA_PRELOADERS.filter((preloader) =>
            preloader.matches(context),
        ).map((preloader) =>
            preloader.load(context, this.apiService).pipe(
                map((data) => ({
                    component: preloader.component,
                    data,
                })),
                catchError(() => of(null)),
            ),
        );

        if (preloadRequests.length === 0) {
            return of([]);
        }

        return forkJoin(preloadRequests).pipe(
            map((preloadedData) =>
                preloadedData.filter(
                    (data): data is PreloadedComponentData => data !== null,
                ),
            ),
        );
    }
}
